import type { Messages } from './fr';

const de: Messages = {
  app: { tagline: 'Das Buchstabenspiel, das mehrere Sprachen spricht.' },
  nav: {
    back: 'Zurück',
    home: 'Start',
    rules: 'Regeln',
    settings: 'Einstellungen',
    about: 'Über',
    account: 'Konto',
    leaderboards: 'Ranglisten',
    tournaments: 'Turniere',
    daily: 'Tagesaufgabe',
    admin: 'Verwaltung',
  },
  common: {
    cancel: 'Abbrechen',
    confirm: 'Bestätigen',
    close: 'Schließen',
    save: 'Speichern',
    loading: 'Wird geladen…',
    yes: 'Ja',
    no: 'Nein',
    copy: 'Kopieren',
    copied: 'Kopiert!',
    retry: 'Erneut versuchen',
  },
  langNames: {
    fr: 'Französisch',
    en: 'Englisch',
    es: 'Spanisch',
    de: 'Deutsch',
    it: 'Italienisch',
    pt: 'Portugiesisch',
  },
  home: {
    quickPlay: 'Spielen',
    quickPlayDesc: 'Klassische Partie, 2 Minuten · {{lang}}',
    modesTitle: 'Spielmodi',
    newGame: 'Neue Partie',
    online: 'Online spielen',
  },
  modes: {
    classic: {
      name: 'Klassisch',
      short: 'Eine Sprache · 2 Min.',
      desc: 'Eine einzige Sprache, für alle gleich. Sammle in 120 Sekunden möglichst viele Punkte. Kein Sprachbonus und kein Bonus für frühes Beenden.',
    },
    polyglot: {
      name: 'Gemeinsam polyglott',
      short: '2 oder 3 Sprachen für alle · 2 Min.',
      desc: 'Dieselben 2 oder 3 Sprachen für alle. Vielfaltsbonus (+20 % mit zwei, +30 % mit drei Sprachen), wenn du in jeder Sprache mindestens 3 Wörter findest.',
    },
    duel: {
      name: 'Taktisches Duell',
      short: '10 Wörter oder 2 Min. · Vertrag',
      desc: 'Jeder wählt geheim 1, 2 oder 3 Sprachen. Ziel: 10 gültige Wörter. Sprachvertrag + Tempobonus, wenn du die 10 Wörter vor Ablauf erreichst. Das 10. Wort beendet deine Runde.',
    },
    sprint: {
      name: 'Freier Sprint',
      short: '1 bis 3 Sprachen · 2 Min. · Vertrag',
      desc: 'Wähle selbst 1 bis 3 Sprachen und sammle in 120 Sekunden möglichst viele Punkte mit Sprachvertrag. Kein Bonus für frühes Beenden.',
    },
    relax: {
      name: 'Entspannt',
      short: 'Ohne Uhr',
      desc: 'Ohne Zeitlimit und ohne Rangliste. Gültigkeitshinweise lassen sich nur in diesem Modus aktivieren.',
    },
  },
  setup: {
    title: 'Neue Partie',
    mode: 'Modus',
    languagesShared: 'Sprachen der Partie ({{min}} bis {{max}})',
    languagesIndividual: 'Deine Sprachen (1 bis 3, vor der Ziehung festgelegt)',
    errors: {
      duplicate_language: 'Jede Sprache kann nur einmal gewählt werden.',
      too_few_languages: 'Wähle mindestens {{min}} Sprache(n).',
      too_many_languages: 'Höchstens {{max}} Sprache(n).',
    },
    noLanguageBonus: 'In diesem Modus gibt es keinen Sprachbonus.',
    opponent: 'Gegner',
    noOpponent: 'Keiner (solo)',
    robotNotice:
      'Roboter sind klar gekennzeichnete Trainingsprogramme. Sie zählen nie in Ranglisten.',
    robotSameLanguages: 'Der Roboter spielt mit meinen Sprachen',
    robotLanguages: 'Sprachen des Roboters',
    loading: 'Ziehung wird vorbereitet…',
    start: 'Starten',
    noDraw:
      'Für diese Kombination wurde keine ausgewogene Ziehung gefunden. Versuche andere Sprachen oder den Spezialisten-Vertrag.',
  },
  contracts: {
    specialist: { name: 'Spezialist', desc: 'eine Sprache, Bonus auf deine Grundpunkte sicher.' },
    duo: {
      name: 'Duo',
      desc: 'zwei Sprachen, Bonus nur mit mindestens 3 verschiedenen Wörtern in jeder.',
    },
    trio: {
      name: 'Trio',
      desc: 'drei Sprachen, Bonus nur mit mindestens 3 verschiedenen Wörtern in jeder.',
    },
    diversity: {
      name: 'Vielfalt',
      desc: '{{pct}} % Bonus für alle bei mindestens 3 Wörtern pro Sprache.',
    },
    short: { specialist: 'Spezialist', duo: 'Duo', trio: 'Trio', diversity: 'Vielfalt' },
  },
  bots: {
    rookie: 'Anfänger',
    casual: 'Mittel',
    skilled: 'Fortgeschritten',
    expert: 'Experte',
    rookieDesc: 'Sehr häufige Wörter, langsames Tempo, bevorzugt kurze Wörter.',
    casualDesc: 'Häufige Wörter, mittleres Tempo.',
    skilledDesc: 'Breiter Grundwortschatz, zügiges Tempo.',
    expertDesc: 'Kennt auch seltene Wörter, schnell, setzt auf lange Wörter.',
    robotName: 'Roboter {{level}}',
  },
  game: {
    timeLeft: 'Restzeit',
    getReady: 'Achtung…',
    untimed: '∞',
    score: 'Punkte',
    points: 'Pkt.',
    wordsOfTarget: '{{n}} / {{target}} Wörter',
    wordsCount_one: '{{count}} Wort',
    wordsCount_other: '{{count}} Wörter',
    languageCounters: 'Wörter pro Sprache',
    robotNotice: 'Roboter-Gegner (Trainingsprogramm)',
    opponentDone: 'fertig',
    finalWarning:
      'Achtung: Dein nächstes gültiges Wort ist das 10. und beendet die Runde. Dein Vertrag ist noch nicht erfüllt.',
    currentWord: 'Aktuelles Wort',
    tapLetters: 'Tippe die Buchstaben an',
    hintValid: 'Gültig: {{langs}}',
    hintInvalid: '(Noch) kein Wort',
    attributeTo: '{{lang}} zuordnen',
    attribution: 'Sprache:',
    auto: 'Auto',
    autoExplain:
      'Auto: Das Wort geht an die gültige Sprache, die für deinen Vertrag am meisten Wörter braucht, sonst nach der Reihenfolge deiner Sprachen.',
    rack: 'Buchstaben der Ziehung',
    tileLabel: 'Buchstabe {{letter}}, Position {{pos}}',
    shuffle: 'Mischen',
    delete: 'Löschen',
    clear: 'Leeren',
    submit: 'Bestätigen',
    foundWords: 'Gefundene Wörter',
    bonusSecured: 'Sprachbonus gesichert',
    finish: 'Beenden',
    stop: 'Aufhören',
    stopTitle: 'Runde beenden?',
    stopBody: 'Deine Wörter bleiben erhalten. Früher aufzuhören bringt keinen Bonus.',
    finishBody: 'Die entspannte Partie beenden und die Bilanz ansehen?',
    confirmFinalTitle: 'Letztes Wort: Vertrag nicht erfüllt',
    confirmFinalOk: 'Trotzdem bestätigen',
    confirmFinalBody:
      'Dieses 10. Wort beendet deine Runde sofort. Dein Sprachvertrag wird nicht erfüllt: kein Sprachbonus (der Tempobonus bleibt).',
    accepted: '„{{word}}“ +{{points}} ({{lang}})',
  },
  reasons: {
    round_over: 'Die Runde ist vorbei.',
    invalid_characters: 'Nicht spielbare Zeichen.',
    too_short: 'Mindestens drei Buchstaben.',
    too_long: 'Wort zu lang.',
    not_formable: 'Mit diesen Buchstaben nicht möglich.',
    duplicate: 'Schon gefunden.',
    language_not_allowed: 'Sprache in dieser Runde nicht erlaubt.',
    excluded_word: 'Wort durch die Inhaltsrichtlinie des Spiels ausgeschlossen.',
    not_a_word: 'Unbekanntes Wort in deinen Sprachen.',
    wrong_language: 'Dieses Wort ist gültig in: {{langs}}.',
    confirmation_required: 'Bestätigung erforderlich.',
    rate_limited: 'Zu viele Versuche, bitte langsamer.',
    not_started: 'Die Runde hat noch nicht begonnen.',
  },
  results: {
    points: 'Punkte',
    pts: 'Pkt.',
    base: 'Grundpunkte',
    fulfilled: 'erfüllt',
    missed: 'verfehlt',
    speedBonus: 'Tempobonus',
    officialTime: 'Offizielle Zeit',
    target: 'Ziel',
    endReason: {
      target_reached: 'Ziel erreicht',
      time_up: 'Zeit abgelaufen',
      stopped: 'Freiwillig beendet',
      abandoned: 'Aufgegeben',
    },
    explainBase: 'Deine Wörter bringen {{base}} Grundpunkte (10 × (Buchstaben − 2)).',
    explainSpecialist: 'Spezialisten-Vertrag: +10 % sicher.',
    explainQuotaOk: 'Mindestens {{quota}} Wörter in jeder Sprache: {{pct}} % Bonus erreicht.',
    explainQuotaKo:
      'Nötig waren mindestens {{quota}} Wörter in jeder Sprache: kein Sprachbonus ({{pct}} % möglich).',
    explainSpeedOk: '10 Wörter in {{time}} erreicht: Tempobonus von {{pct}} %.',
    explainSpeedKo: 'Ziel von 10 Wörtern nicht rechtzeitig erreicht: kein Tempobonus.',
    explainFormula:
      'Gesamt = Grundpunkte × (1 + Sprachbonus + Tempobonus), einmalig auf Hundertstel gerundet.',
    win: 'Sieg!',
    lose: 'Niederlage',
    tie: 'Unentschieden',
    tieExplain: 'Exakt gleiche Punktzahl: Die Begegnung endet unentschieden.',
    you: 'Du',
    robotTitle: '{{name}} (Roboter)',
    missedWords: 'Häufige Wörter, die du bilden konntest',
    missedWordsNote: 'Unverbindliche Liste häufiger Wörter deiner Sprachen, für das nächste Mal.',
    shareText: '{{mode}} ({{langs}}): {{score}} Punkte, {{words}} Wörter. Wer schafft mehr?',
    playAgain: 'Nochmal spielen',
    share: 'Ergebnis teilen',
    changeSetup: 'Modus wechseln',
    localNotice: 'Lokale Partie: zählt in keiner Rangliste.',
    savedToHistory: 'Gespeichert unter „Meine Partien“ (Training).',
    drawAudit: 'Ziehung {{seed}} ({{gen}})',
  },
  errors: {
    network: 'Verbindung verloren. Neuer Versuch…',
    lexiconLoad:
      'Das Wörterbuch konnte nicht geladen werden. Prüfe die Verbindung und versuche es erneut.',
    notFound: 'Seite nicht gefunden',
    generic: 'Ein Fehler ist aufgetreten.',
  },
  online: {
    title: 'Online',
    challengeFriend: 'Freund herausfordern',
    challengeFriendDesc: 'Einladungslink, jeder spielt, wann er will',
    liveDuel: 'Live-Duell',
    liveDuelDesc: 'Raum, Einladung, gleichzeitiges Spiel',
    myGames: 'Meine Partien',
    offlineNote: 'Server nicht erreichbar: Nur Solo und Training sind verfügbar.',
    guestBanner:
      'Du spielst als Gast. Lege ein Konto an, um deinen Verlauf zu behalten und in Ranglisten zu erscheinen.',
    saveAccount: 'Konto sichern',
  },
  challenge: {
    newTitle: 'Freund herausfordern',
    returnMatch: 'Hin- und Rückspiel (2 Runden, getauschte Ziehungen, ohne Wertung)',
    create: 'Herausforderung erstellen',
    created: 'Herausforderung erstellt! Sende diesen Link:',
    share: 'Link teilen',
    shareText: 'Ich fordere dich heraus ({{mode}})!',
    expires: 'Läuft ab am {{date}}',
    playMine: 'Meine Runde jetzt spielen',
    inviteFrom: '{{name}} fordert dich heraus!',
    acceptTitle: 'Einladung',
    chooseLanguages: 'Wähle deine Sprachen (nach Bestätigung festgelegt)',
    compatibleOnly:
      'Es werden nur Kombinationen angeboten, die zur Ziehung dieser Herausforderung passen.',
    accept: 'Herausforderung annehmen',
    expired: 'Diese Herausforderung ist abgelaufen.',
    taken: 'Diese Herausforderung wurde bereits von jemand anderem angenommen.',
    own: 'Das ist deine eigene Herausforderung: Teile den Link mit einem Freund.',
    notFound: 'Herausforderung nicht gefunden.',
    returnInfo:
      'Hin- und Rückspiel: zwei Runden mit zwei verschiedenen Ziehungen; vor dem Ende wird kein Ergebnis gezeigt.',
    goToMatch: 'Zur Partie',
  },
  match: {
    readyTitle: 'Deine Runde',
    readyBody:
      'Die Uhr läuft, sobald die Buchstaben erscheinen. Schließen oder Neuladen der Seite setzt die Zeit nicht zurück.',
    start: 'Meine Runde starten',
    waitingOpponent:
      'Warten auf den Gegner. Seine Wörter und Punkte bleiben bis zum Ende verborgen.',
    opponentProgress: '{{name}}: {{count}} Wort/Wörter',
    leg: 'Runde {{n}} / 2',
    nextLeg: 'Nächste Runde spielen',
    encounterTotal: 'Gesamtstand der Begegnung',
    unranked: 'Partie ohne Wertung',
    ranked: 'Gewertete Partie',
    reportWord: 'Abgelehntes Wort melden',
    reportPrompt: 'Zu meldendes Wort (und Sprache)',
    reportSent: 'Danke, Meldung gesendet.',
    loadError: 'Die Partie konnte nicht geladen werden.',
    opponentNotPlayed: 'Der Gegner hat nicht gespielt.',
    opponent: 'Gegner',
  },
  account: {
    title: 'Mein Konto',
    guest: 'Gastkonto',
    registered: 'Gesichertes Konto',
    register: 'Konto anlegen',
    login: 'Anmelden',
    logout: 'Abmelden',
    username: 'Benutzername (3–20 Zeichen)',
    password: 'Passwort (mind. 10 Zeichen)',
    email: 'E-Mail (optional)',
    displayName: 'Anzeigename',
    saveHint: 'Dein Gastverlauf bleibt beim Anlegen des Kontos erhalten.',
    history: 'Verlauf',
    export: 'Meine Daten exportieren',
    delete: 'Mein Konto löschen',
    deleteConfirm: 'Konto endgültig löschen? Deine persönlichen Daten werden gelöscht.',
    notifications: 'Erinnerungen',
    remindersOptIn: 'Erinnerungen erhalten (höchstens eine pro Tag, nie während der Ruhezeiten)',
    quietHours: 'Ruhezeiten (von … bis …)',
    haveAccount: 'Schon ein Konto?',
    noAccount: 'Noch kein Konto?',
    saved: 'Gespeichert.',
    errors: {
      invalid_credentials: 'Benutzername oder Passwort falsch.',
      username_or_email_taken: 'Benutzername oder E-Mail bereits vergeben.',
      nickname_refused: 'Dieser Name ist nicht erlaubt.',
      nickname_reserved: 'Dieser Name ist reserviert.',
      nickname_length: 'Zwischen 3 und 20 Zeichen.',
      nickname_characters: 'Unerlaubte Zeichen.',
      invalid_request: 'Bitte die Felder prüfen.',
      account_suspended: 'Konto gesperrt.',
      generic: 'Ein Fehler ist aufgetreten.',
    },
  },
  live: {
    searching: 'Gegner wird gesucht…',
    searchingBody: 'Wartezeit: {{seconds}} s',
    noFakePlayers:
      'Hier werden nur echte Spieler vermittelt; kein Roboter wird als Mensch ausgegeben.',
    fallbackTitle: 'Noch niemand da. Du kannst:',
    fallbackRobot: 'Gegen einen Roboter spielen (Training, ohne Wertung)',
    fallbackAsync: 'Eine asynchrone Herausforderung zum Teilen erstellen',
    othersSearching_zero: 'Gerade sucht niemand sonst diese Spielart.',
    othersSearching_one: '{{count}} weitere Person sucht gerade diese Spielart.',
    othersSearching_other: '{{count}} weitere Personen suchen gerade diese Spielart.',
    noOpponentFound: 'Gerade ist niemand verfügbar. Du kannst:',
    searchAgain: 'Erneut suchen',
    languagesSecret: 'Deine Sprachen (geheim, vor der Ziehung festgelegt)',
    quick: 'Schnelle Partie',
    createRoom: 'Raum erstellen und einladen',
    rankedNote:
      'Partien zwischen gesicherten Konten zählen für die Rangliste (offiziell oder experimentell je nach Modus).',
    roomFull: 'Dieser Raum ist voll.',
    roomNotFound: 'Raum nicht gefunden oder abgelaufen.',
    offline: 'getrennt',
    online: 'verbunden',
    opponentReady: 'bereit',
    waitingOpponent: 'Warten auf einen Gegner…',
    shareRoom: 'Lade einen Freund mit diesem Link ein:',
    sharedReady: 'Die Sprachen sind für beide festgelegt.',
    lock: 'Ich bin bereit',
    waitingLock: 'Warten auf die Wahl des Gegners…',
    noDrawForBoth: 'Keine ausgewogene Ziehung für beide Konfigurationen. Wähle andere Sprachen.',
    disconnectRule:
      'Regel bei Verbindungsabbruch: Die Server-Uhr läuft weiter; kehre auf diese Seite zurück, um deine Runde fortzusetzen.',
    opponentLeft:
      '{{name}} ist getrennt. Die Uhr läuft weiter; Rückkehr bis zum Rundenende möglich.',
    opponentBack: '{{name}} ist zurück.',
    reconnecting:
      'Verbindung unterbrochen, neuer Versuch… Bereits bestätigte Wörter bleiben erhalten.',
    rematch: 'Revanche',
    joinRematch: 'Der Revanche beitreten',
    reactionsTitle: 'Reaktionen',
    reactions: {
      gg: 'Gut gespielt!',
      bravo: 'Bravo!',
      wow: 'Beeindruckend!',
      oops: 'Hoppla!',
      thanks: 'Danke!',
      good_luck: 'Viel Glück!',
    },
  },
  boards: {
    title: 'Ranglisten',
    official: 'Offiziell',
    experimental: 'Experimentell',
    officialDesc: 'Symmetrische Modi (Klassisch, Gemeinsam polyglott): gleiche Sprachen für alle.',
    experimentalDesc:
      'Taktisches Duell und Freier Sprint: eigene Rangliste, nicht mit symmetrischen Modi vergleichbar.',
    myRank: 'Dein Rang: {{rank}} · {{rating}} Elo · {{games}} Partien',
    empty: 'Noch keine Ergebnisse.',
    player: 'Spieler',
    rating: 'Elo',
    games: 'Partien',
    rules:
      'Nur Partien zwischen gesicherten Konten zählen. Roboter, lokale Partien und Hin- und Rückspiele erscheinen nie in Ranglisten.',
  },
  tournaments: {
    title: 'Turniere',
    intro:
      'Wöchentliche asynchrone Turniere: mehrere Runden, ein Versuch pro Runde, gleiche Buchstaben für alle.',
    none: 'Derzeit kein Turnier.',
    weeklyTitle: 'Wochenturnier {{mode}} {{langs}} · {{period}}',
    rounds: '{{n}} Runde(n)',
    entrants: '{{n}} Teilnehmer',
    registered: 'angemeldet',
    status: {
      scheduled: 'Demnächst',
      open: 'Läuft',
      closing: 'Abschluss',
      closed: 'Beendet',
      cancelled: 'Abgesagt',
    },
    frozen: 'Regeln {{rules}} und Wörterbücher {{lexicons}} sind für das ganze Turnier festgelegt.',
    attempts: '{{n}} Versuch(e) pro Runde.',
    sharingLimit:
      'Bekannte Grenze: In einem öffentlichen asynchronen Wettbewerb können Spieler Antworten austauschen. Das Spiel behauptet nicht, dies vollständig zu verhindern.',
    register: 'Anmelden',
    accountRequired: 'Für Turniere ist ein gesichertes Konto nötig.',
    myRounds: 'Meine Runden',
    round: 'Runde {{n}}',
    resume: 'Fortsetzen',
    play: 'Spielen',
    standings: 'Turnierstand',
    roundsPlayed: '{{n}} Runde(n)',
    tieRule: 'Exakter Gleichstand: gleicher Rang, kein versteckter Tiebreak.',
    backToTournament: 'Zurück zum Turnier',
    finalStandings: 'Endstand',
    myResult: 'Deine Summe: {{total}} Punkte · Platz {{rank}} von {{n}}',
    noRoundYet: 'Noch keine Runde gespielt.',
    startsAt: 'Beginn: {{date}}',
    abandoned: 'Nicht beendet',
    retry: 'Neuer Versuch ({{n}} übrig)',
    closingNote:
      'Abschluss läuft: begonnene Runden dürfen enden; der Endstand folgt in wenigen Minuten.',
    errors: {
      competition_not_found: 'Turnier nicht gefunden.',
      invitation_required: 'Dieses Turnier ist privat: eine Einladung ist nötig.',
      attempts_exhausted: 'Versuch für diese Runde bereits genutzt.',
      competition_closed: 'Das Turnier ist beendet.',
      competition_not_started: 'Das Turnier hat noch nicht begonnen.',
      not_registered: 'Melde dich zuerst an.',
      account_required: 'Gesichertes Konto erforderlich.',
    },
  },
  daily: {
    title: 'Tagesaufgabe',
    intro: 'Eine Ziehung pro Kategorie und Tag (UTC), für alle gleich. Nur ein Versuch.',
    back: 'Zurück zu den Tagesaufgaben',
    play: 'Spielen',
    inProgress: 'Läuft',
    resets: 'Neue Aufgabe am {{time}}.',
  },
  profile: {
    title: 'Mein Profil',
    streak: 'Tage in Folge',
    bestStreak: 'beste Serie',
    rounds: 'Online-Partien',
    distinctWords: 'verschiedene Wörter',
    streakRules:
      'Die Serie wächst um einen Tag, wenn du eine Online-Partie mit mindestens einem gültigen Wort beendest (Tagesaufgabe, Herausforderung unter Freunden, Live-Duell oder Turnier), höchstens einmal pro Tag. Verbleibende kostenlose Schontage: {{grace}} (alle 30 Tage erneuert). Zeitzone: {{tz}}.',
    useMyTimezone: 'Meine Zeitzone verwenden ({{tz}})',
    tzTooSoon: 'Die Zeitzone kann nur alle 7 Tage geändert werden.',
    badges: 'Abzeichen',
    noBadges: 'Noch keine Abzeichen.',
    badgesNote: 'Abzeichen beruhen nur auf vom Server geprüften Ereignissen.',
    cosmetics: 'Anpassung',
    cosmeticsNote: 'Rein optisch: kein Einfluss auf Punkte oder Ziehungen.',
    plusOnly: 'Nur mit Plus',
    earnIt: 'Durch Spielen freischalten',
    slot: {
      theme: 'Design',
      avatar: 'Avatar',
      frame: 'Rahmen',
    },
    history: 'Meine Partien',
    gamesSummary: '{{online}} online · {{practice}} Training(s)',
    noGames: 'Noch keine gespeicherte Partie.',
    pendingSync: '{{n}} Training(s) warten auf das Senden (offline).',
    gamesNote:
      'Trainings (Solo, Roboter, Entspannt) werden auf deinem Gerät gespielt und hier gespeichert. Der Server prüft die Wörter und berechnet die Punkte neu, kann die Zeit aber nicht prüfen: Sie zählen weder für die Serie noch für Abzeichen oder Ranglisten.',
    sourceOnline: 'Online',
    sourcePractice: 'Training',
    waitingOpponent: 'Ergebnis nach dem Gegner',
    playDaily: 'Tagesaufgabe spielen',
  },
  badges: {
    first_round: 'Erste Runde',
    nine_letters: 'Wort mit 9 Buchstaben',
    trio_contract: 'Trio-Vertrag erfüllt',
    speed_60: '10 Wörter in unter 60 s',
    words_100: '100 Wörter gefunden',
    words_1000: '1000 Wörter gefunden',
    streak_7: 'Serie von 7 Tagen',
    streak_30: 'Serie von 30 Tagen',
    first_duel_win: 'Erster Duellsieg',
    tournament_participant: 'Turnierteilnahme',
    tournament_winner: 'Turniersieg',
    tournament_podium: 'Turnierpodest',
    tournament_top10: 'Turnier-Top-10',
    daily_participant: 'Tagesaufgabe',
    team_weekly: 'Teamziel erreicht',
  },
  cosmetics: {
    theme_default: 'Klassisch',
    theme_ocean: 'Ozean',
    theme_aurora: 'Polarlicht',
    theme_sakura: 'Sakura',
    theme_mono: 'Monochrom',
    avatar_letter: 'Initiale',
    avatar_owl: 'Eule',
    avatar_fox: 'Fuchs',
    frame_none: 'Keiner',
    frame_team: 'Team',
    frame_champion: 'Champion',
  },
  reminders: {
    title: 'Erinnerungen',
    explain:
      'Optional: höchstens eine Erinnerung pro Tag, nie in deinen Ruhezeiten und keine, wenn du schon gespielt hast. Jederzeit abschaltbar.',
    unavailable: 'Erinnerungen sind auf diesem Server oder Gerät nicht verfügbar.',
    quietStart: 'Ruhezeit: Beginn',
    quietEnd: 'Ruhezeit: Ende',
    enable: 'Erinnerungen einschalten',
    disable: 'Ausschalten',
    enabled: 'Erinnerungen an.',
    disabled: 'Erinnerungen aus.',
    denied: 'Vom Browser verweigert.',
  },
  journal: {
    title: 'Wortbuch',
    intro: 'Die Wörter, die du online gefunden hast, nach Sprache.',
    all: 'Alle',
    search: 'Wort suchen',
    empty: 'Noch keine Wörter.',
  },
  friends: {
    title: 'Freunde',
    inbox: 'Erhaltene Herausforderungen',
    inboxNote: 'Öffne den Link deines Freundes, um zu spielen.',
    invite: 'Freund einladen',
    inviteNote:
      'Link 7 Tage gültig. Kein freier Chat: nur Herausforderungen und vordefinierte Reaktionen.',
    none: 'Noch keine Freunde.',
    challenge: 'Herausfordern',
    remove: 'Entfernen',
    ownInvite: 'Das ist deine eigene Einladung.',
    inviteInvalid: 'Ungültige oder abgelaufene Einladung.',
  },
  team: {
    title: 'Team',
    intro:
      'Tritt einem Team bei oder gründe eines (max. 20 Mitglieder) und erreicht gemeinsam ein wöchentliches Wortziel. Nur kosmetische Belohnungen.',
    name: 'Teamname',
    create: 'Team gründen',
    weekly: 'Ziel der Woche {{week}}',
    progress: '{{words}} / {{goal}} Wörter',
    rewardNote: 'Ziel erreicht: Rahmen „Team“ für jedes Mitglied.',
    members: 'Mitglieder ({{n}})',
    leave: 'Team verlassen',
    accountRequired: 'Ein gesichertes Konto ist nötig.',
    joinFailed: 'Beitritt nicht möglich.',
  },
  moderation: {
    block: 'Spieler blockieren',
    blocked: 'Spieler blockiert.',
    report: 'Melden…',
    reported: 'Meldung gesendet.',
    reasons: {
      nickname: 'Unangemessener Name',
      cheating: 'Verdacht auf Betrug',
      harassment: 'Belästigung',
      other: 'Sonstiges',
    },
  },
  plus: {
    intro: 'Plus unterstützt das Spiel und bietet Werkzeuge – ohne je die Punkte zu beeinflussen:',
    fairness:
      'Das Kernspiel, alle sechs Sprachen, Training, Herausforderungen und öffentliche Wettbewerbe bleiben kostenlos. Keine Schwierigkeitseinstellung hängt vom Abo ab.',
    features: {
      advanced_stats: 'Erweiterte Statistiken',
      challenge_archives: 'Vollständiges Archiv beendeter Herausforderungen',
      personal_training: 'Deterministisches persönliches Training',
      customisation: 'Exklusive Designs und Avatare',
      private_tournaments: 'Werkzeuge für private Turniere',
    },
    monthly: 'Abonnieren — {{price}}',
    yearly: 'Jahresabo — {{price}}',
    unavailable: 'Plus kann noch nicht gekauft werden.',
    testMode: 'Testmodus: keine echte Zahlung.',
    confirming: 'Zahlung wird bestätigt… (erst nach Bestätigung des Anbieters freigeschaltet)',
    cancelled: 'Zahlung abgebrochen.',
    active: 'Plus aktiv',
    until: 'bis {{date}}',
    manage: 'Abo verwalten',
    stats: 'Statistiken',
    mode: 'Modus',
    rounds: 'Runden',
    avgScore: 'Ø Punkte',
    contractRate: 'Erfüllte Verträge',
    secPerWord: 's / Wort',
    train: 'Persönliches Training ({{lang}})',
    archive: 'Archiv beendeter Herausforderungen',
    archiveEmpty: 'Noch keine beendete Partie.',
    archiveMore: 'Mehr anzeigen',
    archiveBot: 'Roboter',
    pricesNote:
      'Richtpreise, Änderungen vorbehalten; der genaue Betrag steht auf der Zahlungsseite.',
    notOffered: 'In dieser Version wird kein Abonnement angeboten: Das ganze Spiel ist kostenlos.',
  },
  legal: {
    draftBanner: 'Entwurf — mit der tatsächlichen Identität des Herausgebers zu vervollständigen.',
    privacy: 'Datenschutz',
    terms: 'Nutzungsbedingungen',
    notice: 'Impressum',
  },
  offline: {
    title: 'Offline spielen',
    explain:
      'Lade Wörterbücher herunter, um ohne Verbindung solo zu spielen und zu trainieren. Offline-Partien zählen in keiner Rangliste.',
    download: 'Herunterladen',
    ready: 'Verfügbar',
  },
  play: { noGame: 'Keine laufende Partie.' },
  install: {
    body: 'Installiere das Spiel auf deinem Startbildschirm, um schneller zu spielen – solo auch offline.',
    cta: 'Installieren',
    later: 'Später',
  },
  pwa: {
    updateReady: 'Eine neue Version ist bereit.',
    reload: 'Aktualisieren',
    offline:
      'Offline: Solo und Training bleiben mit den bereits geladenen Wörterbüchern verfügbar.',
  },
  settings: {
    title: 'Einstellungen',
    uiLanguage: 'Sprache der Oberfläche',
    uiLanguageNote:
      'Die Sprache der Oberfläche ist unabhängig von den in deinen Partien erlaubten Sprachen.',
    sound: 'Töne',
    haptics: 'Vibration (falls unterstützt)',
    motion: 'Animationen',
    motionSystem: 'Systemeinstellung',
    motionReduced: 'Reduziert',
    motionFull: 'Normal',
    relaxHints: 'Gültigkeitshinweise im Modus Entspannt',
  },
  rules: {
    title: 'Spielregeln',
    goal: {
      title: 'Ziel',
      body: [
        'Bilde Wörter aus den 9 Buchstaben der Ziehung, indem du sie in beliebiger Reihenfolge antippst – sie müssen nicht nebeneinanderliegen.',
        'Jedes Plättchen zählt nur einmal pro Wort; für das nächste Wort sind wieder alle verfügbar. Mischen ändert die Ziehung nicht.',
        'Mindestens drei Buchstaben. Ein Wort zählt pro Runde nur einmal.',
      ],
    },
    tiles: {
      title: 'Buchstaben und Akzente',
      body: [
        'Ä, Ö und Ü sind eigene Plättchen und nur auf Deutsch nutzbar: „Bär“ und „Bar“ sind verschiedene Wörter. ß wird mit zwei S gelegt: „Straße“ = S-T-R-A-S-S-E.',
        'Akzente anderer Sprachen haben kein eigenes Plättchen (é = E). Ñ ist ein eigenes Plättchen für Spanisch; œ und æ belegen zwei Plättchen.',
      ],
    },
    languages: {
      title: 'Sprachen und Zuordnung',
      body: [
        'Ein Wort, das in mehreren deiner Sprachen gilt, wird nur einer zugeordnet. Wähle die Sprache vor dem Bestätigen (Schaltflächen „Sprache“) oder lass „Auto“.',
        'Auto wählt die gültige Sprache, die für deinen Vertrag am meisten Wörter braucht, dann die Reihenfolge deiner Sprachen. Ein angenommenes Wort wechselt nie die Sprache.',
        'Eigennamen und Abkürzungen sind ausgeschlossen. Manche beleidigenden Wörter schließt die Inhaltsrichtlinie aus. Abgelehnte Wörter wirst du melden können.',
      ],
    },
    points: {
      title: 'Punkte',
      body: [
        'Ein Wort mit n Buchstaben (Plättchen) bringt 10 × (n − 2): 3 Buchstaben = 10, 4 = 20 … 9 = 70.',
        'Endstand = Grundpunkte × (1 + Sprachbonus + Tempobonus). Boni werden addiert, das Ergebnis wird einmalig auf Hundertstel gerundet.',
        'Ein abgelehntes Wort bringt nichts und zählt nicht für das Ziel.',
      ],
    },
    contracts: {
      title: 'Verträge (Taktisches Duell, Freier Sprint)',
      body: [
        'Spezialist (1 Sprache): +10 %. Duo (2 Sprachen): +20 % mit mindestens 3 Wörtern in jeder. Trio (3 Sprachen): +30 % mit mindestens 3 Wörtern in jeder.',
        'Verfehlter Vertrag: Die Grundpunkte bleiben, ohne Sprachbonus und ohne automatischen Wechsel zu einem kleineren Vertrag.',
        'Gemeinsam polyglott: +20 % (2 Sprachen) oder +30 % (3 Sprachen) bei mindestens 3 Wörtern pro Sprache, für alle gleich.',
      ],
    },
    speed: {
      title: 'Tempobonus (Taktisches Duell)',
      body: [
        'Erreichst du 10 gültige Wörter vor 120 s: Bonus = 20 % × (120 − t) / 120, wobei t die vom Server gemessene offizielle Zeit ist. Bei 90 s: +5 %; bei 60 s: +10 %; bei 30 s: +15 %.',
        'Das 10. angenommene Wort beendet deine Runde sofort. Das Spiel warnt dich, wenn dein Vertrag nicht erfüllt ist.',
      ],
    },
    modes: {
      title: 'Modi',
      body: [
        'Klassisch, Gemeinsam polyglott, Taktisches Duell, Freier Sprint, Entspannt (ohne Uhr und Rangliste) sowie Training gegen Roboter.',
        'Exakter Gleichstand: unentschieden, ohne versteckten Tempo-Tiebreak.',
      ],
    },
    fairness: {
      title: 'Fairness',
      body: [
        'Dieselbe Ziehung ist nicht in allen Sprachen gleich schwer. Jede Ziehung wird geprüft, damit jede Konfiguration genug häufige Wörter hat – perfekte Fairness wird nicht versprochen.',
        'Die Parameter sind Hypothesen im Test; eine Partie behält immer die Regel- und Wörterbuchversion, mit der sie begonnen hat.',
      ],
    },
    robots: {
      title: 'Roboter',
      body: [
        'Roboter sind deterministische Programme (keine generative KI) und als solche gekennzeichnet. Sie spielen nur gültige Wörter und erscheinen nie in menschlichen Ranglisten.',
      ],
    },
    keyboard: {
      title: 'Tastatur',
      body: 'Buchstaben tippen (oder 1–9 für die Position eines Plättchens), Eingabe zum Bestätigen, Rücktaste zum Löschen, Esc zum Leeren, Leertaste zum Mischen.',
    },
  },
  about: {
    title: 'Über das Spiel',
    disclaimer:
      'Ein Buchstabenspiel aus Freude am Spielen in mehreren Sprachen. Eine pädagogische Wirksamkeit ist nicht belegt.',
    dictionaries: 'Wörterbücher und Daten',
    frequencies: 'Worthäufigkeiten',
    policy: 'Inhaltsrichtlinie',
    adapted: 'angepasste Liste',
    noticeFiles:
      'Vollständige Hinweise (Quellen, Versionen, Lizenzen) liegen jedem geladenen Wörterbuch bei.',
    noDefinitions:
      'Definitionen und Übersetzungen werden erst angezeigt, wenn eine Quelle mit passender Lizenz eingebunden ist.',
  },
};

export default de;
